import { authStore } from '@services/authStore.service';
import { environment } from '@config/environment.config';
import type { RunAnalysisEntity, RunAnalysisUIPermissions, RunAnalysisUpdatePayload } from '@/types/run-stage.types';

const ILA_API_BASE = environment.getIlaApiBaseUrl();

async function handleResponse<T>(res: Response, url: string): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    throw new Error(`Unauthorized request to ${url}`);
  }
  if (res.status >= 500) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Server error on ${url}: ${text}`);
  }

  const response = await res.json();
  
  // API returns { success, message, data }
  // Extract the data property
  const data = response.data || response;
  
  return data as T;
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authStore.getAuthHeader(),
    },
    body: JSON.stringify(body),
  });

  return handleResponse<T>(res, url);
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'GET',
    headers: authStore.getAuthHeader(),
  });

  return handleResponse<T>(res, url);
}

/**
 * Get a RunAnalysis with populated graph snapshot
 */
export async function getRunAnalysis(analysisId: string): Promise<RunAnalysisEntity> {
  return get<RunAnalysisEntity>(`/api/v2/runAnalysis/get/${analysisId}`);
}

/**
 * Update RunAnalysis: status, graph, procedure, metadata, comments
 */
export async function updateRunAnalysis(body: RunAnalysisUpdatePayload): Promise<RunAnalysisEntity> {
  return post<RunAnalysisEntity>('/api/v2/runAnalysis/update', body);
}

/**
 * Get UI permissions and available actions for RunAnalysis
 */
export async function getRunAnalysisUIPermissions(analysisId: string): Promise<RunAnalysisUIPermissions> {
  return post<RunAnalysisUIPermissions>('/api/v2/runAnalysis/ui', { _id: analysisId });
}

/**
 * Save analysis as new version (fork with deep cloned graph)
 */
export async function saveAsRunAnalysis(body: {
  fromAnalysisId: string;
  procedure?: unknown;
  isStepsOptimized?: boolean;
  graph?: unknown;
  allComments?: unknown[];
}): Promise<RunAnalysisEntity> {
  return post<RunAnalysisEntity>('/api/v2/runAnalysis/saveAs', body);
}

/**
 * Archive a RunAnalysis
 */
export async function archiveRunAnalysis(analysisId: string): Promise<RunAnalysisEntity> {
  return post<RunAnalysisEntity>('/api/v2/runAnalysis/archive', { _id: analysisId });
}

/**
 * Restore an archived RunAnalysis
 */
export async function restoreRunAnalysis(analysisId: string): Promise<RunAnalysisEntity> {
  return post<RunAnalysisEntity>('/api/v2/runAnalysis/restore', { _id: analysisId });
}

/**
 * Delete (soft delete) a RunAnalysis
 */
export async function deleteRunAnalysis(analysisId: string): Promise<RunAnalysisEntity> {
  return post<RunAnalysisEntity>('/api/v2/runAnalysis/delete', { _id: analysisId });
}
